import React, { useCallback, useRef, useState } from 'react';
import { FiPlus, FiChevronDown, FiX, FiFileText } from 'react-icons/fi';
import '../styles/DocumentPanel.css';

export interface DocumentItem {
  id: string;
  fileName: string;
  createdDate?: string;
  status?: string;
}

interface DocumentPanelProps {
  title: string;
  subtitle: string;
  documents: DocumentItem[];
  loading: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onDownload?: (id: string) => Promise<void>;
  onClose: () => void;
}

const DocumentPanel: React.FC<DocumentPanelProps> = ({
  title,
  subtitle,
  documents,
  loading,
  onUpload,
  onDelete,
  onDownload,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files);
      if (arr.length === 0) return;
      setUploading(true);
      try {
        await onUpload(arr);
      } finally {
        setUploading(false);
      }
    },
    [onUpload]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDeleteClick = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await onDelete(id);
      } finally {
        setDeletingId(null);
      }
    },
    [onDelete]
  );

  return (
    <div className="doc-panel">
      <div className="doc-panel__header">
        <div>
          <h3 className="doc-panel__title">{title}</h3>
          <p className="doc-panel__subtitle">{subtitle}</p>
        </div>
        <button className="doc-panel__close" onClick={onClose}>
          <FiX size={18} />
        </button>
      </div>

      <div
        className={`doc-panel__dropzone${dragOver ? ' doc-panel__dropzone--active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <FiPlus size={32} className="doc-panel__dropzone-icon" />
        <p className="doc-panel__dropzone-text">Drop files here or click to browse</p>
        <p className="doc-panel__dropzone-hint">Supports PDF, Images (PNG, JPEG), CSV, Excel files</p>
        <button
          className="doc-panel__browse-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FiPlus size={14} />
          {uploading ? <><span className="doc-panel__btn-spinner" /> Uploading...</> : 'Choose Files'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.csv,.xls,.xlsx,.doc,.docx"
          className="doc-panel__file-input"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="doc-panel__list">
        {loading ? (
          <div className="doc-panel__loading">
            <div className="doc-panel__spinner" />
            <span>Loading documents...</span>
          </div>
        ) : documents.length === 0 ? (
          <div className="doc-panel__empty">No documents uploaded yet</div>
        ) : (
          documents.map((doc) => {
            const formattedDate = doc.createdDate ? (() => {
              try {
                const d = new Date(doc.createdDate);
                if (isNaN(d.getTime())) return '';
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              } catch { return ''; }
            })() : '';
            return (
              <div key={doc.id} className="doc-panel__item">
                <FiFileText size={16} className="doc-panel__item-icon" />
                <div className="doc-panel__item-info">
                  <span className="doc-panel__item-name" title={doc.fileName}>
                    {doc.fileName}
                  </span>
                  {formattedDate && (
                    <span className="doc-panel__item-date">{formattedDate}</span>
                  )}
                </div>
                <div className="doc-panel__item-actions">
                  {onDownload && (
                    <button
                      className="doc-panel__item-btn doc-panel__item-btn--download"
                      onClick={() => onDownload(doc.id)}
                      title="Download"
                    >
                      <FiChevronDown size={14} />
                    </button>
                  )}
                  <button
                    className="doc-panel__item-btn doc-panel__item-btn--delete"
                    onClick={() => handleDeleteClick(doc.id)}
                    disabled={deletingId === doc.id}
                    title="Delete"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DocumentPanel;
